import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Slide, Fade } from "@mui/material";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Stack from "react-bootstrap/Stack";

import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import ListHeading from "../../../common/RDTListHeading/ListHeading";
import GeneralButton from "../../../common/SaveButton/GeneralButton";
import Images from "../../../../utils/images";
import { changeTitle } from "../../../../state/slices/header";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { showError, showSuccess } from "../../../../utils/showMessage";
import { createScanConfig, updateScanConfig } from "../../../../api/credentialScanning";
import "../index.scss";

// Brand/protocol names (SSH, RDP, MySQL, MSSQL, PostgreSQL, Oracle, Linux,
// Windows) and file patterns (.env, *.tf, etc.) stay verbatim; only descriptive
// wrappers translate. `key` and `value` fields are stable identifiers.

const ENTROPY_LEVEL_DEFS = [
  { max: 2.5, key: "credentialScanning.entropyLevelVeryLow",  color: "#43a047" },
  { max: 3.5, key: "credentialScanning.entropyLevelLow",      color: "#8bc34a" },
  { max: 4.5, key: "credentialScanning.entropyLevelMedium",   color: "#f9a825" },
  { max: 5.0, key: "credentialScanning.entropyLevelHigh",     color: "#f57c00" },
  { max: Infinity, key: "credentialScanning.entropyLevelVeryHigh", color: "#e53935" },
];

const getEntropyLevel = (v, t) => {
  const def = ENTROPY_LEVEL_DEFS.find((d) => v <= d.max);
  return { label: t(def.key), color: def.color };
};

const ASSET_TYPE_DEFS = [
  { value: "ssh",        labelKey: "credentialScanning.assetTypeSshLinux" },
  { value: "rdp",        labelKey: "credentialScanning.assetTypeRdpWindows" },
  { value: "mysql",      label: "MySQL" },
  { value: "mssql",      label: "MSSQL" },
  { value: "postgresql", label: "PostgreSQL" },
  { value: "oracle",     label: "Oracle" },
];

const SCAN_CATEGORY_DEFS = [
  { key: "web_app_config", labelKey: "credentialScanning.catWebAppConfigLabel", descKey: "credentialScanning.catWebAppConfigDesc", icon: "🌐" },
  { key: "dev_devops",     labelKey: "credentialScanning.catDevDevopsLabel",    descKey: "credentialScanning.catDevDevopsDesc",    icon: "⚙️" },
  { key: "iac",            labelKey: "credentialScanning.catIacLabel",          descKey: "credentialScanning.catIacDesc",          icon: "🏗️" },
  { key: "scripting",      labelKey: "credentialScanning.catScriptingLabel",    descKey: "credentialScanning.catScriptingDesc",    icon: "📝" },
  { key: "db_cloud_cli",   labelKey: "credentialScanning.catDbCloudCliLabel",   descKey: "credentialScanning.catDbCloudCliDesc",   icon: "🗄️" },
  { key: "system_os",      labelKey: "credentialScanning.catSystemOsLabel",     descKey: "credentialScanning.catSystemOsDesc",     icon: "🔒" },
];

function CreateEditConfigPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const ASSET_TYPE_OPTIONS = useMemo(
    () => ASSET_TYPE_DEFS.map((d) => ({ value: d.value, label: d.labelKey ? t(d.labelKey) : d.label })),
    [t]
  );
  const SCAN_CATEGORY_OPTIONS = useMemo(
    () => SCAN_CATEGORY_DEFS.map((d) => ({
      key: d.key, icon: d.icon, label: t(d.labelKey), description: t(d.descKey),
    })),
    [t]
  );

  const config = location.state?.config || null;
  const isEdit = !!config;

  const [slideChecked, setSlideChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    description: "",
    scan_all_pam_assets: true,
    scan_git_repos: false,
    asset_types: [],
    enable_regex: true,
    enable_entropy: true,
    enable_pam_matching: true,
    entropy_threshold: 3.5,
    scan_categories: [],
    max_file_size_kb: 512,
    schedule_enabled: false,
    schedule_interval_hours: 24,
  });

  const MAX_FILE_SIZE_KB = 512;

  useEffect(() => {
    dispatch(changeTitle("Discovery"));
    document.title = isEdit ? t("credentialScanning.editScanConfiguration") : t("credentialScanning.newScanConfiguration");
    setSlideChecked(true);

    if (config) {
      setForm({
        name: config.name || "",
        description: config.description || "",
        scan_all_pam_assets: config.scan_all_pam_assets ?? true,
        scan_git_repos: config.scan_git_repos ?? false,
        asset_types: config.asset_types || [],
        enable_regex: config.enable_regex ?? true,
        enable_entropy: config.enable_entropy ?? true,
        enable_pam_matching: config.enable_pam_matching ?? true,
        entropy_threshold: config.entropy_threshold ?? 3.5,
        scan_categories: config.scan_categories || [],
        max_file_size_kb: Math.min(config.max_file_size_kb ?? 512, MAX_FILE_SIZE_KB),
        schedule_enabled: config.schedule_enabled ?? false,
        schedule_interval_hours: config.schedule_interval_hours ?? 24,
      });
    }
  }, [config, isEdit, dispatch, t]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleAssetType = (type) => {
    setForm((prev) => {
      const types = prev.asset_types.includes(type)
        ? prev.asset_types.filter((t) => t !== type)
        : [...prev.asset_types, type];
      return { ...prev, asset_types: types };
    });
  };

  const toggleCategory = (key) => {
    setForm((prev) => {
      const cats = prev.scan_categories.includes(key)
        ? prev.scan_categories.filter((k) => k !== key)
        : [...prev.scan_categories, key];
      return { ...prev, scan_categories: cats };
    });
  };

  const toggleAllCategories = () => {
    if (form.scan_categories.length === SCAN_CATEGORY_OPTIONS.length) {
      set("scan_categories", []);
    } else {
      set("scan_categories", SCAN_CATEGORY_OPTIONS.map((o) => o.key));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!form.name.trim()) {
      setErrors({ name: t("credentialScanning.errPleaseProvideName") });
      return;
    }

    if (!form.enable_regex && !form.enable_entropy && !form.enable_pam_matching) {
      setErrors({ detection_methods: t("credentialScanning.errSelectAtLeastOneDetectionMethod") });
      showError(t("credentialScanning.errSelectAtLeastOneDetectionMethod"));
      return;
    }

    const maxSize = form.max_file_size_kb;
    if (!Number.isInteger(maxSize) || maxSize < 1) {
      setErrors({ max_file_size_kb: t("credentialScanning.errMaxFileSizeGtZero") });
      return;
    }

    if (form.schedule_enabled) {
      const hours = form.schedule_interval_hours;
      if (!Number.isInteger(hours) || hours < 1) {
        setErrors({ schedule_interval_hours: t("credentialScanning.errIntervalGtZero") });
        return;
      }
    }

    setSaving(true);

    const payload = { ...form };
    if (isEdit) payload.id = config.id;

    const fn = isEdit ? updateScanConfig : createScanConfig;
    const { error: err } = await fn(payload);
    setSaving(false);

    if (err) {
      if (typeof err === "object" && !err.detail) {
        setErrors(err);
      } else {
        showError(err?.detail || t("credentialScanning.toastFailedToSaveConfig"));
      }
    } else {
      showSuccess(isEdit ? t("credentialScanning.toastConfigUpdated") : t("credentialScanning.toastConfigCreated"));
      navigate("/dashboard/discovery/hardcoded-credentials/configs");
    }
  };

  const allCatsSelected = form.scan_categories.length === SCAN_CATEGORY_OPTIONS.length;
  const noCatsSelected = form.scan_categories.length === 0;

  return (
    <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
      <Fade timeout={fadedelayTime} in={slideChecked}>
        <div className="main_content_container p-3 w-100">
          <div className="position-relative bg-white p-4" style={{ minHeight: "calc(100vh - 120px)" }}>

            {/* Breadcrumb */}
            <div className="py-2 pt-2">
              <BreadcrumbHeading
                dataTableHeadingText={[
                  t("credentialScanning.breadcrumbBackTo"),
                  t("credentialScanning.scanConfigurations"),
                  isEdit ? t("credentialScanning.editConfiguration") : t("credentialScanning.newConfiguration"),
                ]}
                dataTablePageLink={[
                  "/dashboard/discovery/hardcoded-credentials",
                  "/dashboard/discovery/hardcoded-credentials/configs",
                  isEdit
                    ? `/dashboard/discovery/hardcoded-credentials/configs/${config?.id}/edit`
                    : "/dashboard/discovery/hardcoded-credentials/configs/new",
                ]}
                pageNumber={5}
              />
            </div>

            <div className="mb-4">
              <ListHeading
                dataTableHeadingText={isEdit ? t("credentialScanning.editScanConfiguration") : t("credentialScanning.newScanConfiguration")}
                dataTableHeadingIcon={Images.NetworkAnalyzer}
              />
            </div>

            <Form onSubmit={handleSubmit}>
              <Container fluid className="px-0">
                <Row>
                  {/* Left column */}
                  <Col xs={12} md={6} className="pe-md-4">

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.fieldName")} <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        required
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder={t("credentialScanning.placeholderProductionLinuxServers")}
                        isInvalid={!!errors.name}
                      />
                      {errors.name && <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.fieldDescription")}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={form.description}
                        onChange={(e) => set("description", e.target.value)}
                        placeholder={t("credentialScanning.placeholderOptionalDescription")}
                      />
                    </Form.Group>

                    <div className="cred-scan-form-section mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.fieldScanTargets")}</Form.Label>
                      <div className="d-flex flex-column gap-2">
                        <Form.Check
                          type="switch"
                          label={t("credentialScanning.toggleScanAllPamResources")}
                          checked={form.scan_all_pam_assets}
                          onChange={(e) => set("scan_all_pam_assets", e.target.checked)}
                        />
                        <Form.Check
                          type="switch"
                          label={t("credentialScanning.toggleScanGitRepos")}
                          checked={form.scan_git_repos}
                          onChange={(e) => set("scan_git_repos", e.target.checked)}
                        />
                      </div>
                    </div>

                    {form.scan_all_pam_assets && (
                      <div className="cred-scan-form-section mb-3">
                        <Form.Label className="fw-semibold">{t("credentialScanning.fieldAssetTypes")} <span className="text-muted fw-normal">{t("credentialScanning.hintEmptyAll")}</span></Form.Label>
                        <div className="d-flex flex-wrap gap-2 mt-1">
                          {ASSET_TYPE_OPTIONS.map((opt) => {
                            const sel = form.asset_types.includes(opt.value);
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => toggleAssetType(opt.value)}
                                className={`cred-scan-type-chip ${sel ? "selected" : ""}`}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="cred-scan-form-section mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.fieldDetectionMethods")} <span className="text-danger">*</span></Form.Label>
                      <div className="d-flex flex-column gap-2">
                        {[
                          { key: "enable_regex", label: t("credentialScanning.detLabelRegexPatterns") },
                          { key: "enable_entropy", label: t("credentialScanning.detLabelHighEntropyAnalysis") },
                          { key: "enable_pam_matching", label: t("credentialScanning.detLabelPamAccountMatching") },
                        ].map(({ key, label }) => (
                          <Form.Check
                            key={key}
                            type="switch"
                            label={label}
                            checked={form[key]}
                            onChange={(e) => set(key, e.target.checked)}
                          />
                        ))}
                      </div>
                      {errors.detection_methods && (
                        <div className="text-danger small mt-1">{errors.detection_methods}</div>
                      )}
                    </div>

                    {form.enable_entropy && (
                      <div className="cred-scan-form-section mb-3">
                        <Form.Label className="fw-semibold">
                          {t("credentialScanning.fieldEntropyThreshold")} —{" "}
                          <span style={{ color: getEntropyLevel(form.entropy_threshold, t).color, fontWeight: 700 }}>
                            {form.entropy_threshold.toFixed(1)} · {getEntropyLevel(form.entropy_threshold, t).label}
                          </span>
                        </Form.Label>
                        <Form.Range
                          className="cred-scan-entropy-slider"
                          min="1.0" max="6.0" step="0.1"
                          value={form.entropy_threshold}
                          onChange={(e) => set("entropy_threshold", parseFloat(e.target.value))}
                        />
                        <div className="d-flex justify-content-between">
                          <small className="text-muted">{t("credentialScanning.entropyLessStrict")}</small>
                          <small className="text-muted">{t("credentialScanning.entropyMoreStrict")}</small>
                        </div>
                      </div>
                    )}

                    <div className="cred-scan-form-section mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.fieldMaxFileSizeKB")} <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={MAX_FILE_SIZE_KB}
                        value={form.max_file_size_kb}
                        onChange={(e) => set("max_file_size_kb", Math.min(MAX_FILE_SIZE_KB, parseInt(e.target.value)))}
                        style={{ maxWidth: 140 }}
                        isInvalid={!!errors.max_file_size_kb}
                      />
                      {errors.max_file_size_kb && <Form.Control.Feedback type="invalid">{errors.max_file_size_kb}</Form.Control.Feedback>}
                      <Form.Text className="text-muted">{t("credentialScanning.helperMaxAllowedSize", { size: MAX_FILE_SIZE_KB })}</Form.Text>
                    </div>

                    <div className="cred-scan-form-section mb-3">
                      <Form.Label className="fw-semibold">{t("credentialScanning.fieldSchedule")}</Form.Label>
                      <Form.Check
                        type="switch"
                        label={t("credentialScanning.toggleEnableScheduledScanning")}
                        checked={form.schedule_enabled}
                        onChange={(e) => set("schedule_enabled", e.target.checked)}
                        className="mb-2"
                      />
                      {form.schedule_enabled && (
                        <>
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">{t("credentialScanning.runEvery")}</span>
                            <Form.Control
                              type="number"
                              min={1}
                              max={168}
                              step={1}
                              value={form.schedule_interval_hours}
                              onChange={(e) => {
                                const raw = e.target.value;
                                set("schedule_interval_hours", raw === "" ? "" : parseInt(raw, 10));
                              }}
                              style={{ width: 90 }}
                              isInvalid={!!errors.schedule_interval_hours}
                            />
                            <span className="text-muted small">{t("credentialScanning.hoursUnit")}</span>
                          </div>
                          {errors.schedule_interval_hours && (
                            <div className="text-danger small mt-1">{errors.schedule_interval_hours}</div>
                          )}
                        </>
                      )}
                    </div>

                  </Col>

                  {/* Right column — File scan categories */}
                  <Col xs={12} md={6}>
                    <div className="mb-1 d-flex align-items-center justify-content-between">
                      <Form.Label className="fw-semibold mb-0">{t("credentialScanning.fieldFileScanCategories")}</Form.Label>
                      <button
                        type="button"
                        onClick={toggleAllCategories}
                        style={{
                          fontSize: 12, padding: "3px 12px",
                          border: "1px solid #5177ff", borderRadius: 4,
                          background: "#fff", color: "#5177ff",
                          cursor: "pointer", fontWeight: 600,
                        }}
                      >
                        {allCatsSelected ? t("credentialScanning.btnDeselectAll") : t("credentialScanning.btnSelectAll")}
                      </button>
                    </div>

                    {noCatsSelected && (
                      <p className="text-muted small mb-2">
                        {t("credentialScanning.helperNoCategoriesSelected")}
                      </p>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {SCAN_CATEGORY_OPTIONS.map((cat) => {
                        const checked = form.scan_categories.includes(cat.key);
                        return (
                          <div
                            key={cat.key}
                            onClick={() => toggleCategory(cat.key)}
                            className={`cred-scan-category-card ${checked ? "selected" : "unselected"}`}
                            style={{
                              display: "flex", alignItems: "flex-start", gap: 8,
                              padding: "10px 12px", borderRadius: 8,
                              cursor: "pointer",
                              transition: "border-color 0.15s, background 0.15s",
                              border: `1.5px solid ${checked ? "#5177ff" : "#e0e0e0"}`,
                              background: checked ? "#f0f4ff" : "#fafafa",
                            }}
                          >
                            <Form.Check
                              type="checkbox"
                              checked={checked}
                              onChange={() => {}}
                              style={{ marginTop: 2, flexShrink: 0, accentColor: "#5177ff" }}
                            />
                            <div>
                              <div className="cat-label" style={{ fontSize: 12, fontWeight: 600, color: checked ? "#3456cc" : "#333" }}>
                                {cat.icon} {cat.label}
                              </div>
                              <div className="cat-desc" style={{ fontSize: 11, color: "#777", marginTop: 2, lineHeight: 1.4 }}>
                                {cat.description}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </Row>

                {/* Global error display */}
                {errors.detail && (
                  <div className="mt-3 p-3 rounded" style={{ background: "#ffebee", color: "#c62828", fontSize: 13 }}>
                    {errors.detail}
                  </div>
                )}

                {/* Action buttons */}
                <Row className="mt-4">
                  <Col className="d-flex justify-content-end">
                    <Stack direction="horizontal" gap={3}>
                      <GeneralButton
                        onClickEvent={() => navigate("/dashboard/discovery/hardcoded-credentials/configs")}
                        variant="outlined"
                        value={t("credentialScanning.btnCancel")}
                        size="large"
                      />
                      <GeneralButton
                        type="submit"
                        onClickEvent={handleSubmit}
                        variant="contained"
                        value={saving ? t("credentialScanning.btnSavingEllipsis") : isEdit ? t("credentialScanning.btnUpdateConfiguration") : t("credentialScanning.btnCreateConfiguration")}
                        size="large"
                        disabled={saving}
                      />
                    </Stack>
                  </Col>
                </Row>
              </Container>
            </Form>
          </div>
        </div>
      </Fade>
    </Slide>
  );
}

export default CreateEditConfigPage;
